import { CommonModule } from '@angular/common';
import { Component, inject, Input, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController, ToastController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { fieldSorter, LoadingComponent } from '@scandium-oy/ngx-scandium';
import { combineLatest } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { Message } from 'src/app/models/message.model';
import { SubItem } from 'src/app/models/sub-item.model';
import { SelectDialogComponent } from 'src/app/select-dialog/select.dialog';
import { AreasService } from 'src/app/services/areas.service';
import { MessagesService } from 'src/app/services/messages.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { UsersService } from 'src/app/services/users.service';
import { getUniqueGuid } from 'src/app/utility/list';
import { Roles } from 'src/app/utility/role';

@Component({
  selector: 'app-new-message',
  templateUrl: './new-message.component.html',
  styleUrls: ['./new-message.component.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    LoadingComponent,
  ],
})
export class NewMessageComponent {
  private areasService = inject(AreasService);
  private messagesService = inject(MessagesService);
  private modalCtrl = inject(ModalController);
  private navigationService = inject(NavigationService);
  private toastCtrl = inject(ToastController);
  private translate = inject(TranslateService);
  private usersService = inject(UsersService);

  @Input()
  set message(value: Message) {
    this.selectedTo.set([value.sender]);
    this.formGroup.get('msgId').setValue(value.msgId);
  }

  formGroup: FormGroup;

  users$ = this.usersService.getCurrentUser().pipe(
    filter((user) => user != null),
    switchMap((user) => user.role === Roles.resident ? this.usersService.getMyList('admins') : combineLatest([this.usersService.getMyList(), this.areasService.getMyAreas()]).pipe(
      map(([users, areas]) => users.filter((u) => u.role === Roles.resident).filter((u) => areas.some((a) => a.access?.includes(u.guid)))),
    )),
    map((users) => users.map((u) => ({ guid: u.guid, name: u.displayName }))),
    map((users) => users.sort(fieldSorter(['name']))),
    shareReplay(1),
  );

  selectedTo = signal<SubItem[]>([]);

  constructor(
    formBuilder: FormBuilder,
  ) {
    this.formGroup = formBuilder.group({
      feedback: ['', Validators.required],
      msgId: [null],
    });
  }

  private getMsgId() {
    const user = this.usersService.currentUserS();
    return combineLatest([
      this.messagesService.getMsgId({ sender: user.guid, users: this.selectedTo().map((it) => it.guid) }),
      ...this.selectedTo().map((u) =>
        this.messagesService.getMsgId({ sender: u.guid, users: [user.guid, ...this.selectedTo().map((it) => it.guid).filter((it) => it !== u.guid)] })),
    ]).pipe(
      map((msgs) => {
        const msgIds = msgs.filter((it) => it?.msgId != null);
        if (msgIds.length > 0) {
          return msgIds[0].msgId;
        }
        return null;
      }),
    );
  }

  selectUser(items: SubItem[]) {
    const clearButton = this.selectedTo().length > 0;
    this.modalCtrl.create({ component: SelectDialogComponent, componentProps: { items, clearButton, multiple: true } }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (data.data.clear) {
            this.selectedTo.set([]);
          } else {
            this.selectedTo.set([...data.data]);
          }
        }
      });
    });
  }

  save() {
    const user = this.usersService.currentUserS();
    this.getMsgId().pipe(
      take(1),
    ).subscribe((msgId) => {
      const { feedback } = this.formGroup.value;
      const message: Message = {
        title: '',
        body: feedback,
        users: this.selectedTo().map((it) => it.guid),
        reads: this.selectedTo().map((it) => ({ guid: it.guid, read: false })),
        sender: {
          name: user.displayName,
          guid: user.guid,
        },
        msgId: msgId ?? getUniqueGuid(),
      };
      this.messagesService.save(message).then(() => {
        this.formGroup.reset();
        this.selectedTo.set([]);
        this.toastCtrl.create({ message: this.translate.instant('messages.messageSent'), duration: 3000 }).then(async (t) => t.present());
        this.navigationService.navigate(['/worker', 'messages'], { queryParams: { tab: 'sent' } });
      });
    });
  }
}
